<template>
  <BaseLayout title="Account" subtitle="Manage your profile" :narrow="false">
    <div v-if="ready" class="account-wrap">
      <div v-if="justActivated" class="notice ok">Subscription activated 🎉 You’re on Standard.</div>

      <!-- Signed-out -->
      <section v-if="!isSignedIn" class="card">
        <h2 class="card-title">You’re signed out</h2>
        <p class="muted">Sign in to view and manage your profile and subscription.</p>
        <div class="row-gap">
          <button class="btn" @click="openAuth('signin')">Sign In</button>
          <button class="btn btn-outline" @click="openAuth('signup')">Create Account</button>
        </div>
      </section>

      <!-- Signed-in -->
      <template v-else>
        <!-- Profile -->
        <section class="card">
          <h2 class="card-title">Profile</h2>

          <!-- Email + Username on one row -->
          <div class="grid-2">
            <div class="kv">
              <div class="kv-label">Email</div>
              <div class="kv-value">{{ user?.email }}</div>
            </div>
            <div class="kv">
              <div class="kv-label">Username</div>
              <div class="kv-value">{{ profile?.username || '—' }}</div>
            </div>
          </div>

          <!-- Display name (editable) -->
          <div class="field">
            <label>Display name</label>
            <div class="inline">
              <input v-model="newDisplayName" class="input" placeholder="Your name" />
              <button class="btn" :disabled="busy || !newDisplayName" @click="onUpdateName">Save</button>
            </div>
          </div>

          <!-- Plan (authoritative single block) -->
          <div class="kv">
            <div class="kv-label">Plan</div>
            <div class="kv-value kv-stack">
              <span>{{ plan }}</span>

<!-- Paid: manage subscription/billing -->
<div v-if="plan !== 'free'" class="row-gap">
  <!-- iOS with active IAP: Apple manage button -->
  <template v-if="isNativeIOS && hasActiveIap">
    <button type="button" class="btn-primary" @click="openNativeManage">Manage subscription</button>
    <span class="hint">Opens Apple’s subscription management.</span>
  </template>

  <!-- iOS without IAP (likely Stripe on web): neutral note, no Stripe link -->
  <template v-else-if="isNativeIOS && !hasActiveIap">
    <span class="hint">This subscription was purchased on the web. Manage it from your website account page.</span>
  </template>

  <!-- Web/Android: Stripe portal -->
  <template v-else>
    <button type="button" class="btn-primary" @click="openBillingPortal">Manage billing</button>
    <span class="hint">Cancel or update payment in the secure Stripe portal.</span>
  </template>
</div>

              <!-- Free: show upgrade link -->
              <router-link v-else class="link" to="/pricing">View plan options</router-link>
            </div>
          </div>

          <!-- Sign out -->
          <div class="actions">
            <button class="btn btn-outline" @click="onSignOut">Sign out</button>
          </div>

          <div v-if="msgProfile" class="notice ok">{{ msgProfile }}</div>
          <div v-if="errProfile" class="notice error">{{ errProfile }}</div>
        </section>

        <!-- Security -->
        <section class="card">
          <h2 class="card-title">Security</h2>

          <div class="field">
            <label>Password</label>
            <div class="inline">
              <input value="••••••••" class="input input-static" readonly />
              <button class="btn" :disabled="busy" @click="onSendReset">Email reset link</button>
            </div>
            <p class="hint">
              We’ll send a password reset link to <strong>{{ user?.email }}</strong>.
            </p>

            <!-- Reset notice under action -->
            <div v-if="msgReset" class="notice ok">
              {{ msgReset }} Please check your inbox and your spam/junk folder.
            </div>
            <div v-if="errReset" class="notice error">{{ errReset }}</div>
          </div>

          <hr class="soft" />

          <div id="danger" class="danger">
            <div class="danger-head">
              <h3>Danger zone</h3>
              <p class="muted">
                This permanently removes your sign-in. Calculations may remain anonymized for analytics.
              </p>
            </div>
            <button class="btn btn-danger-outline btn-sm" :disabled="busy" @click="onDelete">
              Delete account
            </button>
          </div>
        </section>
      </template>
    </div>

    <!-- skeleton while store initializes -->
    <div v-else class="skeleton">
      <div class="skeleton-line"></div>
      <div class="skeleton-line long"></div>
      <div class="skeleton-card"></div>
    </div>

    <!-- Inline auth modal for convenience -->
    <AuthModal
      :open="showAuth"
      :initial-mode="authMode"
      title="Welcome"
      @close="showAuth=false"
      @signed-in="onSignedIn"
    />
  </BaseLayout>
</template>

<script setup>
import { ref, onMounted, watch, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import BaseLayout from '@/components/BaseLayout.vue'
import AuthModal from '@/components/AuthModal.vue'
import { useAuthStore } from '@/stores/auth'
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { app } from "@/firebase/firebase";
import { Capacitor } from '@capacitor/core'
import * as Purchases from '@revenuecat/purchases-capacitor'


const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { user, profile, ready } = storeToRefs(auth)

const plan = computed(() => auth.currentUser?.plan || profile.value?.plan || 'free')
const justActivated = computed(() => route.query.checkout === 'success')

const fns = getFunctions(app, import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || "us-central1");
const isNativeIOS =
  typeof Capacitor?.getPlatform === 'function' &&
  Capacitor.getPlatform() === 'ios' &&
  typeof Capacitor?.isNativePlatform === 'function' &&
  Capacitor.isNativePlatform()

const manageUrl = ref(null)
const hasActiveIap = ref(false)

onMounted(async () => {
  if (isNativeIOS) {
    try {
      const info = await Purchases.getCustomerInfo()
       // active entitlements = IAP subscription present
      const active = info?.customerInfo?.entitlements?.active || {}
      hasActiveIap.value = Object.keys(active).length > 0
      const url =
        info && info.customerInfo && info.customerInfo.managementURL
          ? String(info.customerInfo.managementURL)
          : null
      manageUrl.value = url || 'https://apps.apple.com/account/subscriptions'
    } catch (e) {
      manageUrl.value = 'https://apps.apple.com/account/subscriptions'
    }
  }
})

function openNativeManage () {
  const url = manageUrl.value || 'https://apps.apple.com/account/subscriptions'
  window.location.href = url
}
async function openBillingPortal() {
  const returnUrl = window.location.origin + "/account";
  try {
    const fn = httpsCallable(fns, "createBillingPortalSession");
    const { data } = await fn({ returnUrl });
    if (data && data.url) {
      window.location.href = data.url;
      return;
    }
  } catch (_) {}

  try {
    const u = getAuth().currentUser;
    if (!u) throw new Error("Please sign in first.");
    const idToken = await u.getIdToken();
    const resp = await fetch("https://us-central1-robot-cycle-time-ai.cloudfunctions.net/createBillingPortalSessionHttp", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ returnUrl }),
    });
    if (resp.ok) {
      const { url } = await resp.json();
      if (url) { window.location.href = url; return; }
    }
  } catch (_) {}

  // Fallback if neither function exists (shouldn’t happen after portal deploy)
  alert("Billing portal is unavailable right now. Please try again shortly.");
}

onMounted(() => {
  auth.init()
  if (route.query.tab === 'delete') {
    nextTick(() => document.getElementById('danger')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
})

const isSignedIn = computed(() => !!user.value)

const newDisplayName = ref('')
watch([ready, profile], () => {
  if (ready.value && profile.value) {
    newDisplayName.value = profile.value.displayName || user.value?.displayName || ''
  }
}, { immediate: true })

const busy = ref(false)
// Separate notices for profile vs reset
const msgProfile = ref(''); const errProfile = ref('')
const msgReset = ref('');   const errReset = ref('')
function clearNotices(){ msgProfile.value=''; errProfile.value=''; msgReset.value=''; errReset.value='' }

async function onUpdateName(){
  if (!newDisplayName.value) return
  busy.value = true; errProfile.value=''; msgProfile.value=''
  try {
    await auth.updateDisplayName(newDisplayName.value)
    msgProfile.value = 'Display name updated.'
  } catch (e) {
    errProfile.value = e?.message || 'Could not update name.'
  } finally {
    busy.value = false
  }
}

async function onSendReset(){
  if (!user.value?.email) return
  busy.value = true; errReset.value=''; msgReset.value=''
  try {
    await auth.sendReset(user.value.email)
    msgReset.value = `Password reset link sent to ${user.value.email}.`
  } catch (e) {
    errReset.value = e?.message || 'Could not send reset link.'
  } finally {
    busy.value = false
  }
}

async function onDelete(){
  if (!confirm('Delete your account? This cannot be undone.')) return
  busy.value = true; clearNotices()
  try {
    await auth.deleteAccount()
    router.replace('/')
  } catch (e) {
    errProfile.value = e?.message || 'Delete failed.'
  } finally {
    busy.value = false
  }
}

async function onSignOut(){
  busy.value = true; clearNotices()
  try { await auth.signOut() } finally { busy.value = false }
}

// Local auth modal for convenience
const showAuth = ref(false)
const authMode = ref('signin')
function openAuth(mode){ authMode.value = mode || 'signin'; showAuth.value = true }
function onSignedIn(){ showAuth.value = false }
</script>

<style scoped>
/* Center + wider stack (calculator/route feel) */
.account-wrap{
  max-width: 980px;
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

/* Cards */
.card{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:12px;
  box-shadow:0 10px 30px rgba(16,24,40,.06);
  padding:20px;
}
.card-title{ font-size:1.05rem; font-weight:800; margin:0 0 12px; }

/* Key/Value rows (non-editable) */
.kv{ display:grid; grid-template-columns: 1fr; gap:4px; margin-bottom:12px; }
.kv-label{ font-size:.85rem; color:#6b7280; font-weight:600; }
.kv-value{ font-size:.95rem; color:#111827; font-weight:500; word-break: break-word; }
.kv-row{ display:flex; align-items:center; gap:10px; }
.kv-stack{ display:flex; flex-direction:column; gap:4px; }

/* Editable fields */
.field{ display:grid; gap:6px; margin-bottom:12px; }
.field > label{ font-size:.85rem; color:#6b7280; font-weight:600; }
.inline{ display:flex; gap:10px; align-items:center; }
.input{
  height:36px; width:100%;
  border:1px solid #d4dae4; border-radius:8px; padding:0 10px; font-size:13px;
  outline:0; transition:border .15s, box-shadow .15s;
}
.input:focus{ border-color:#111827; box-shadow:0 0 0 3px rgba(17,24,39,.12); }
.input-static{ background:#f3f4f6; }

/* Two-up rows */
.grid-2{
  display:grid; gap:14px; grid-template-columns: 1fr;
}
@media (min-width: 720px){
  .grid-2{ grid-template-columns: 1fr 1fr; }
}

/* Buttons: white on black, smaller type */
.btn{
  display:inline-flex; align-items:center; justify-content:center;
  height:36px; padding:0 12px; border:1px solid #111827;
  border-radius:8px; background:#111827; color:#fff;
  font-weight:700; font-size:13px; cursor:pointer;
}
.btn:hover{ background:#0b1220; border-color:#0b1220; }
.btn:disabled{ opacity:.7; cursor:not-allowed; }

.btn-outline{
  background:#fff; color:#111827; border-color:#111827;
}
.btn-outline:hover{ background:#f6f7f9; }

.btn-danger-outline{
  background:#fff; color:#b91c1c; border-color:#b91c1c;
}
.btn-danger-outline:hover{ background:#fff5f5; border-color:#991b1b; color:#991b1b; }

.btn-sm{ height:32px; padding:0 10px; font-size:12px; }

.row-gap{ display:flex; gap:10px; margin-top:10px; }
.actions{ margin-top:8px; }

/* Link */
.link{ color:#111827; text-decoration:underline; font-weight:600; font-size:.92rem; width:max-content; }

/* Text helpers */
.hint{ font-size:.9rem; color:#5b667a; margin:6px 0 0; }
.muted{ color:#5b667a; }

/* Notices */
.notice{ margin-top:10px; border-radius:8px; padding:8px 10px; font-size:.92rem; }
.notice.ok{ background:#ecfdf5; color:#065f46; }
.notice.error{ background:#fef2f2; color:#b91c1c; }

/* Divider + Danger */
.soft{ border:0; border-top:1px solid #edf0f5; margin:16px 0; }
.danger{ display:grid; gap:12px; }
.danger-head h3{ margin:0; font-size:.98rem; }

/* Skeleton */
.skeleton{ max-width:980px; margin:0 auto; padding:20px; }
.skeleton-line{ height:14px; width:220px; background:#e5e7eb; border-radius:8px; margin:6px 0; }
.skeleton-line.long{ width:360px; }
.skeleton-card{ height:140px; border-radius:12px; background:linear-gradient(90deg,#ececec,#f7f7f7,#ececec); background-size:200% 100%; animation:shimmer 1.3s linear infinite; }
@keyframes shimmer{ 0%{background-position:0 0} 100%{background-position:-200% 0} }

/* Pricing-style black CTA (matches your Pricing.vue) */
.btn-primary {
  background: #111827; color: #fff; border: 1px solid #111827;
  border-radius: 10px; padding: .625rem 1rem; cursor: pointer;
}
.btn-primary:hover { opacity: .92; }
</style>
